<template>
  <div
    class="tag"
    :class="{
      disabled: disabled,
      active: active,
      [`tag-${variantColor}`]: variantColor,
      'tag-rounded': rounded,
      'tag-pricing': pricingUp || pricingDown,
      'up': pricingUp,
      'down': pricingDown,
      'tag-selectable': selectable,
      'tag-avatar': avatar,
      'tag-removable': removable,
    }"
    @click="onClick"
  >
    <template v-if="pricingUp || pricingDown">
      <span>
        <slot />
      </span>
    </template>

    <img
      v-else-if="selectable && avatar"
      :src="avatar"
      alt="Avatar"
      width="24"
      height="24"
    >

    <slot v-if="!(pricingUp || pricingDown)" />

    <icon
      v-if="removable"
      @click.native="remove"
      icon="remove-small"
    />
  </div>
</template>

<script>
// import SvgIcon from 'vue-svgicon/dist/components/svgicon.common'

export default {
  components: {
    // SvgIcon,
  },
  props: {
    /**
     * Applies one of the color variants to the component.
     */
    variant: {
      type: String,
      default: 'grey',
    },

    /**
     * Avatar URL for the selectable mode.
     */
    avatar: {
      type: String,
      default: undefined,
    },

    /**
     * Applies the rounded shape to the component.
     */
    rounded: {
      type: Boolean,
      default: false,
    },

    /**
     * Applies the pricing-up shape to the component.
     */
    pricingUp: {
      type: Boolean,
      default: null,
    },

    /**
     * Applies the pricing-down shape to the component.
     */
    pricingDown: {
      type: Boolean,
      default: false,
    },

    /**
     * Applies the selectable shape to the component.
     */
    selectable: {
      type: Boolean,
      default: false,
    },

    /**
     * Applies the removable shape to the component.
     */
    removable: {
      type: Boolean,
      default: false,
    },

    /**
     * Applies the active state to the component.
     */
    active: {
      type: Boolean,
      default: false,
    },

    /**
     * Tag does not react on mouse events.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    variantColor () {
      if (this.selectable || this.pricingDown || this.pricingUp) {
        return null
      } else {
        return this.variant
      }
    },
  },
  methods: {
    onClick (e) {
      this.$emit('click', e)
    },
    remove (e) {
      this.$emit('remove', e)
    },
  },
}
</script>

<style lang="scss">
@import "./variables";

.tag {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: normal;
  height: 24px;
  max-height: 24px;
  border-radius: 2px;
  text-align: center;
  line-height: 1;
  padding: 0.407rem 0.5rem;
  color: $gray-900;

  &.disabled {
    pointer-events: none;
    cursor: default;
    user-select: none;
  }

  &:focus,
  &.focus {
    box-shadow: 0 0 0 2px $cyan-500;
  }

  @each $color, $value in $colors-standard-active {
    &.tag-#{$color} {
      background-color: $value;

      &.active,
      &.active:hover,
      &.active.hover {
        color: #fff;
        background-color: $gray-900;
      }

      &.disabled {
        color: $gray-300;
        background-color: $gray-200;
      }

      &:hover,
      &.hover {
        color: $gray-900;
        background-color: map-get($colors-standard-hover, $color);
      }
    }
  }

  &-rounded {
    @each $color, $value in $colors-rounded-active {
      &.tag-#{$color} {
        color: $value;
        border-color: $value;
        background-color: #fff;
        border-radius: 20px;
        border: 1px solid $value;

        &.active,
        &.disabled,
        &:hover,
        &.hover {
          color: $value;
          border-color: $value;
          background-color: #fff;
        }
      }
    }
  }

  &-pricing {
    font-family: $font-family-monospace !important;
    font-size: 12px;
    height: 16px;
    max-height: 16px;
    padding: 0;
    background-color: #fff !important;
    border-radius: 1px;
    position: relative;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;

    &:before {
      display: block;
      height: 16px;
      width: 16px;
      position: absolute;
      left: 0;
      top: 0;
    }

    > span {
      padding: 0 0.25rem;
      margin-left: 0.25rem;
      border-radius: 1px;
      height: 100%;
      display: inline-flex;
      align-items: center;
      margin-left: 20px;
    }

    &.down {
      &:before {
        content: escape-svg(
          url("data:image/svg+xml,<svg height='16' viewBox='0 0 16 16' width='16' xmlns='http://www.w3.org/2000/svg'><path d='m6 8-6-8h12z' fill='#ff6256' transform='translate(2 4)'/></svg>")
        );
      }

      > span {
        background-color: #ffc1bc;
      }
    }

    &.up {
      &:before {
        content: escape-svg(
          url("data:image/svg+xml,<svg height='16' viewBox='0 0 16 16' width='16' xmlns='http://www.w3.org/2000/svg'><path d='m6 0 6 8h-12z' fill='#00beb1' transform='translate(2 4)'/></svg>")
        );
      }

      > span {
        background-color: #b8f9f4;
      }
    }
  }

  &-selectable {
    background-color: transparent;
    border-radius: 20px;
    border: 1px solid $gray-900;
    position: relative;
    cursor: pointer;
    user-select: none;

    &:hover,
    &.hover {
      color: $cyan-700;
      border-color: $cyan-700;
      border-width: 1px;
    }

    &.disabled {
      color: $gray-300;
      border-color: $gray-200;
    }

    &.active {
      color: #fff;
      background-color: $gray-900;
      padding-left: 27px;

      &:not(.tag-avatar):before {
        content: escape-svg(
          url("data:image/svg+xml,<svg height='16' viewBox='0 0 16 16' width='16' xmlns='http://www.w3.org/2000/svg'><path d='m11.695 1.707-7.707 7.707-3.707-3.707 1.414-1.414 2.293 2.293 6.293-6.293z' fill='#fff' fill-rule='evenodd' transform='translate(2 3.2)'/></svg>")
        );
        position: absolute;
        display: block;
        height: 16px;
        width: 16px;
        left: 6px;
        top: 3px;
      }
    }

    &.tag-avatar {
      height: 32px;
      max-height: 32px;
      padding: 0.25rem;
      padding-right: 0.5rem;

      &.disabled {
        img {
          opacity: 0.4;
        }
      }

      img {
        margin-right: 0.5rem;
        border-radius: 50%;
      }
    }
  }

  &-removable {
    .i {
      margin-top: -2px;
      margin-left: 0.5rem;
      cursor: pointer;
    }
  }
}
</style>
